import { statsProcess, statsResult } from '@/services/exam'
import { debounce } from 'lodash'
import router from '@/routers'
import { getFromsessionStorage } from '@/services/utils'

const statsProcessActions = {
  click: debounce(statsProcess, 19),
  input: debounce(statsProcess, 399)
}

const processEventMap = {
  enter_page: '进入页面',
  click_txtbutton: '点击文字录入按钮',
  input_VAR_txt: '作答区VAR-录入文字',
  input_VAR1_VAR2_txt: '作答区VAR1-VAR2-录入文字',
  clickinput_VAR_audio: '点击语音录入按钮-作答区VAR录入语音',
  input_VAR_del: '作答区VAR-被删除',
  click_next: '点击下一题',
  input_txt: '作答区文字录入VAR',
  clickinput_audio: '点击语音录入VAR',
  start_record: '点击录音VAR',
  end_record: '结束录音VAR',
  done_task: '点击作答完成按钮',
  next_task: '点击下一个任务按钮',
  questionnaire_submit: '问卷点击提交',
}

const parseStatsVar = (statsVar) => {
  const pairs = String(statsVar).split('#')
  const var1 = pairs[0]
  const var2 = pairs[1] || ''
  const var3 = pairs[2] || ''
  return {
    VAR: var1 + var2 + var3,
    VAR1: var1,
    VAR2: var2,
    VAR3: var3
  }
}

const replaceVars = (text, vars) => {
  return text.replace('VAR1', vars.VAR1 || '')
    .replace('VAR2', vars.VAR2 || '')
    .replace('VAR3', vars.VAR3 || '')
    .replace('VAR', vars.VAR || '') // 最后匹配短的
}

const dontStats = () => router.currentRoute.meta.stats === false

export const postProcessStats = (data, type = 'click') => {
  // 禁止发送统计数据
  if (dontStats()) {
    return
  }
  const note = data.note || processEventMap[data.event] || ''
  const vars = parseStatsVar(data.statsVar || '')
  const oldEvent = data.event
  const category = sessionStorage.getItem('examCategory') || null

  data.event = replaceVars(data.event, vars)
  data.note = replaceVars(note, vars)
  data.category = category

  // 对 event 做特殊处理，若 VAR 有值，而 event 中没有被替换的子串，则将 event 后加 _VAR
  if (vars.VAR && data.event == oldEvent) {
    data.event = data.event + '_' + vars.VAR
  }

  delete data.statsVar
  if (data.topic == null || data.topic == '') {
    data.topic = sessionStorage.getItem('statsCurrentTopic')
  }
  statsProcessActions[type](data)
}

export const parseStatsData = string => {
  const pairs = string.split('#')
  return {
    event: pairs[0],
    note: pairs[1] || '',
    // 给 checkbox 用
    event2: pairs[2] || '',
    note2: pairs[3] || '',
  }
}

let initialized = false

const emitProcessStats = (/** @type HTMLElement */el, type = 'click') => {
  const value = el.dataset[type + 'Stats']
  const elData = typeof value === 'string' ? parseStatsData(value) : value

  // 若元素上有明确的 data-stats-note，则它的优先级最高，甚至比 data-click-stats 中的值还要高，要注意
  const note = el.dataset.statsNote
  if (note) {
    elData.note = note
  }

  // 对 checkbox 进行特殊处理
  if (el.classList.contains('el-checkbox')) {
    // is-checked 是点击后才添加的，这里监听的是捕捉阶段的 click 事件，
    // 所以若此时为 is-checked，则说明接下来要取消选择
    if (el.classList.contains('is-checked')) {
      elData.event = elData.event2
      elData.note = elData.note2
    }
  }

  delete elData.event2
  delete elData.note2

  // 对图片放大、缩小进行特殊处理
  if (elData.event == 'img_zoom_in') {
    const [type, name = ''] = (el.dataset.imageKind || '').split('#')
    elData.event = elData.event + (type ? '_' + type : '')
    elData.note = `点击${name}放大按钮`
  } else if (elData.event == 'img_zoom_out') {
    const [type, name = ''] = (el.dataset.imageKind || '').split('#')
    elData.event = elData.event + (type ? '_' + type : '')
    elData.note = `点击${name}缩小按钮`
  }

  const topicEl = el.closest('[data-stats-topic]')
  const topic = topicEl ? topicEl.dataset.statsTopic : ''
  const statsVar = el.dataset.statsVar
  const data = { topic, ...elData, statsVar }
  postProcessStats(data, type)
}

// 添加全局事件监听，埋点统计
export const initProcessStats = () => {
  if (!initialized) {
    initialized = true
    document.body.addEventListener(
      'click',
      event => {
        const target = event.target
        if (target) {
          const el = target.closest('[data-click-stats]')
          if (el && el.dataset && 'clickStats' in el.dataset) {
            emitProcessStats(el)
          }
        }
      },
      true
    )
    document.body.addEventListener(
      'input',
      event => {
        const el = event.target
        if (el && el.dataset && 'inputStats' in el.dataset) {
          emitProcessStats(el, 'input')
        }
      },
      true
    )
  }
}

export const getStatsResultKey = () => {
  const userId = getFromsessionStorage('userInfo').id
  const kind = getFromsessionStorage('STATS_COMMON').kind
  return `STATS_RESULT_USER_${userId}_KIND_${kind}`
}

const getValue = el => {
  let value = ''
  const tagName = el.tagName
  if (el.dataset.statsResult == 'radio') {
    const radio = el.querySelector('.el-radio.is-checked')
    value = {
      text: radio ? radio.innerText : '',
      number: radio ? radio.dataset.number : ''
    }
  } else if (el.dataset.statsResult == 'checkbox') {
    value = []
    ;(el.querySelectorAll('.el-checkbox.is-checked') || []).forEach(v => {
      const text = v.innerText
      const number = v.dataset.number
      value.push({ text, number })
    })
  } else if (el.dataset.statsResult == 'bubble') {
    value = []
    el.querySelectorAll('.bubble_list').forEach(list => {
      const input = list.querySelector('.bubble_title input')
      if (input != null) {
        const values = []
        list.querySelectorAll('.bubble_answer_list [data-contenteditable]').forEach(sub => {
          values.push(sub.innerText)
        })
        value.push({ title: input.value, values })
      }
    })
  } else if (tagName === 'INPUT' || tagName === 'TEXTAREA') {
    value = el.value
  } else {
    value = el.innerText
  }
  return value
}

export const saveStepResult = box => {
  if (dontStats()) {
    return
  }
  const el = box || document.body
  const groupData = {}
  // 匹配任务 A，2-3 题在一起的情形，这里要分开
  const otherTopicsGroupData = {}
  el.querySelectorAll('[data-stats-result], [data-contenteditable]').forEach((/** @type HTMLElement */item, itemIndex) => {
    if (item.hasAttribute('data-contenteditable')) {
      const result = item.closest('[data-stats-result]')
      if (result != null && result.dataset.statsResult == 'bubble') {
        // 忽略 bubble 内的 contenteditable
        console.info('🔥忽略 bubble 内的 contenteditable')
        return
      }
    }
    const value = getValue(item)
    if (item.hasAttribute('data-stats-result')) {
      // 是否有明确的 data-stats-topic，应对匹配任务 A，2-3 题在一起的情形
      const statsTopic = item.dataset.statsTopic
      if (statsTopic) {
        // 这样写是准备应对 2+ 个题目的情形
        if (otherTopicsGroupData[statsTopic] == null) {
          otherTopicsGroupData[statsTopic] = {}
        }
        otherTopicsGroupData[statsTopic][itemIndex] = value
      } else {
        // 对 data-stats-result 单独处理，注意 data-stats-result 就不要再 group 了
        groupData[itemIndex] = value
      }
    } else {
      const group = item.closest('[data-stats-result-group]')
      const gname = group ? group.dataset.statsResultGroup : '###'
      if (groupData[gname] == null) {
        groupData[gname] = []
      }
      groupData[gname].push(value)
    }
  })
  const stepData = processStepData(groupData)
  const key = getStatsResultKey()
  const result = getFromsessionStorage(key)
  const topic = sessionStorage.getItem('statsCurrentTopic')
  result[topic] = stepData
  sessionStorage.setItem(key, JSON.stringify(result))

  // 在同一个页面，分开保存其他题型的题目
  for (const [topic, topicGroupData] of Object.entries(otherTopicsGroupData)) {
    const topicStepData = processStepData(topicGroupData)
    result[topic] = topicStepData
    sessionStorage.setItem(key, JSON.stringify(result))
  }
}

const processStepData = (groupData) => {
  let stepData = []
  for (const key in groupData) {
    stepData.push(groupData[key])
  }
  // console.log('💢💢💢 stepData', stepData)
  // 展平数据，因为大部分只有一个
  if (stepData.length == 1) {
    stepData = stepData[0]
  }
  return stepData
}

const KEY_SURVEY = 'Survey-'

export const saveSurveyResult = (box, surveyNo) => {
  if (dontStats()) {
    return
  }
  const el = box || document.body

  const values = []
  el.querySelectorAll('[data-result-item]').forEach(div => {
    const type = div.dataset.resultItem
    switch (type) {
      case 'radio':
        const radio = div.querySelector('.el-radio.is-checked')
        values.push({
          type,
          text: radio ? String(radio.innerText).trim() : '',
          number: radio ? radio.dataset.number : ''
        })
        break
      case 'checkbox':
        const checkboxValues = []
        div.querySelectorAll('.el-checkbox.is-checked').forEach(group => {
          const label = group.querySelector('.el-checkbox__label')
          if (label) {
            const input = label.querySelector('.el-input__inner')
            const text = String(label.innerText).trim()
            const number = group.dataset.number
            if (input) {
              const note = String(input.value).trim()
              checkboxValues.push({ text, number, note })
            } else {
              checkboxValues.push({ text, number })
            }
          }
        })
        values.push({ type, list: checkboxValues })
        break
      case 'text':
        const input = div.querySelector('input,[contenteditable]')
        if (input && !('ignore' in input.dataset)) {
          values.push({ type, text: String(input.innerText).trim() })
        }
        break
    }
  })

  const key = getStatsResultKey()
  const result = getFromsessionStorage(key)

  const topic = surveyNo || (sessionStorage.getItem('statsCurrentTopic') || '').split('-')[0]
  result[KEY_SURVEY + topic] = values
  sessionStorage.setItem(key, JSON.stringify(result))
}

export const postResultStats = () => {
  if (dontStats()) {
    return
  }
  const key = getStatsResultKey()
  const result = getFromsessionStorage(key)
  statsResult(result).then(() => {
    sessionStorage.removeItem(key)
  })
}
